import type { SimulationState, Command, CommandResult, ThemeData, GameSpeed } from '../core/types';
import type { GameEvent, GameEventType } from '../core/events/GameEvents';
import type { SeededRandom } from '../core/rng/SeededRandom';
import { processCommand } from '../core/simulation/CommandProcessor';
import { GameLoop } from '../core/simulation/GameLoop';

type StateSelector = string; // e.g., 'company', 'staff', 'projects', 'company.funds'
type StateCallback = (newValue: unknown, oldValue: unknown) => void;
type EventCallback = (event: GameEvent) => void;
type Unsubscribe = () => void;

interface Subscription {
  selector: StateSelector;
  callback: StateCallback;
}

interface EventSubscription {
  eventType: GameEventType;
  callback: EventCallback;
}

/**
 * GameStore: the bridge between the simulation core and the UI/renderer.
 *
 * - dispatch() sends commands to the simulation
 * - subscribe() watches for state changes on specific slices
 * - on() listens for game events
 * - tick() advances the simulation and notifies all subscribers
 */
export class GameStore {
  private state: SimulationState;
  private themeData: ThemeData;
  private rng: SeededRandom;
  private gameLoop: GameLoop;

  private stateSubscriptions: Subscription[] = [];
  private eventSubscriptions: EventSubscription[] = [];
  private previousSnapshot: Record<string, unknown> = {};
  private devMode: boolean;

  constructor(
    state: SimulationState,
    themeData: ThemeData,
    rng: SeededRandom,
    gameLoop: GameLoop,
    devMode: boolean = false,
  ) {
    this.state = state;
    this.themeData = themeData;
    this.rng = rng;
    this.gameLoop = gameLoop;
    this.devMode = devMode;
    this.takeSnapshot();
  }

  /** Read the current simulation state (read-only view). */
  getState(): Readonly<SimulationState> {
    return this.state;
  }

  /** Get the loaded theme data. */
  getThemeData(): Readonly<ThemeData> {
    return this.themeData;
  }

  /** Get the current game speed. */
  getSpeed(): GameSpeed {
    return this.state.speed;
  }

  /**
   * Subscribe to changes on a specific state slice.
   * Selector is a dot-separated path: 'company', 'staff', 'company.funds', etc.
   * Callback fires when the top-level slice reference changes.
   */
  subscribe(selector: StateSelector, callback: StateCallback): Unsubscribe {
    const sub: Subscription = { selector, callback };
    this.stateSubscriptions.push(sub);

    return () => {
      const index = this.stateSubscriptions.indexOf(sub);
      if (index !== -1) {
        this.stateSubscriptions.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to a specific game event type.
   * Callback fires when that event is emitted during a tick.
   */
  on(eventType: GameEventType, callback: EventCallback): Unsubscribe {
    const sub: EventSubscription = { eventType, callback };
    this.eventSubscriptions.push(sub);

    return () => {
      const index = this.eventSubscriptions.indexOf(sub);
      if (index !== -1) {
        this.eventSubscriptions.splice(index, 1);
      }
    };
  }

  /**
   * Dispatch a player command to the simulation.
   * The command is validated and executed immediately.
   */
  dispatch(command: Command): CommandResult {
    const { result, events } = processCommand(this.state, command, this.themeData, this.rng);

    if (result.success && events.length > 0) {
      this.notifyStateSubscribers();
      this.notifyEventSubscribers(events);
    }

    return result;
  }

  /**
   * Advance the simulation by one tick.
   * Notifies all subscribers of state changes and events.
   * Returns the events that occurred.
   */
  tick(): GameEvent[] {
    const events = this.gameLoop.tick(this.state, this.themeData, this.rng);

    if (events.length > 0 || this.hasStateChanged()) {
      this.notifyStateSubscribers();
    }

    if (events.length > 0) {
      this.notifyEventSubscribers(events);
    }

    this.takeSnapshot();
    return events;
  }

  /** Replace the simulation state (for save/load). */
  replaceState(newState: SimulationState, rng: SeededRandom): void {
    // Migrate old saves missing new fields
    if (!newState.activeRecruiter) newState.activeRecruiter = null;
    if (!newState.loans) newState.loans = [];
    if (!newState.purchasedEquipment) newState.purchasedEquipment = [];
    if (!newState.achievements) newState.achievements = [];
    if (!newState.timeline) newState.timeline = [];
    if (!newState.marketTrends) newState.marketTrends = {};
    this.state = newState;
    this.rng = rng;
    this.takeSnapshot();
    // Notify all subscribers of the wholesale state change
    this.notifyAllStateSubscribers();
  }

  /** Get the current RNG for save purposes. */
  getRng(): SeededRandom {
    return this.rng;
  }

  /** Returns count of active subscriptions (for debugging). */
  get subscriptionCount(): number {
    return this.stateSubscriptions.length + this.eventSubscriptions.length;
  }

  /** Check for leaked subscriptions in dev mode. */
  warnIfLeaked(expectedMax: number = 50): void {
    if (this.devMode && this.subscriptionCount > expectedMax) {
      console.warn(
        `GameStore: ${this.subscriptionCount} subscriptions active (expected < ${expectedMax}). Possible leak.`,
      );
    }
  }

  // ─── Private ─────────────────────────────────────────────

  private takeSnapshot(): void {
    this.previousSnapshot = {
      company: this.state.company,
      staff: this.state.staff,
      teams: this.state.teams,
      projects: this.state.projects,
      products: this.state.products,
      contracts: this.state.contracts,
      competitors: this.state.competitors,
      office: this.state.office,
      hiringPool: this.state.hiringPool,
      contractPool: this.state.contractPool,
      unlocks: this.state.unlocks,
      speed: this.state.speed,
      isPaused: this.state.isPaused,
    };
  }

  private hasStateChanged(): boolean {
    return (
      this.state.company !== this.previousSnapshot['company'] ||
      this.state.staff !== this.previousSnapshot['staff'] ||
      this.state.projects !== this.previousSnapshot['projects'] ||
      this.state.products !== this.previousSnapshot['products'] ||
      this.state.office !== this.previousSnapshot['office']
    );
  }

  private getSliceValue(selector: string): unknown {
    const parts = selector.split('.');
    let current: unknown = this.state;
    for (const part of parts) {
      if (current === null || current === undefined || typeof current !== 'object') return undefined;
      current = (current as Record<string, unknown>)[part];
    }
    return current;
  }

  private getOldSliceValue(selector: string): unknown {
    const topLevel = selector.split('.')[0]!;
    return this.previousSnapshot[topLevel];
  }

  private notifyStateSubscribers(): void {
    for (const sub of this.stateSubscriptions) {
      const newValue = this.getSliceValue(sub.selector);
      const oldValue = this.getOldSliceValue(sub.selector);

      // For top-level selectors, check reference equality
      // For nested selectors, always fire (since we can't cheaply deep-compare)
      const topLevel = sub.selector.split('.')[0]!;
      if (sub.selector === topLevel) {
        if (newValue !== oldValue) {
          sub.callback(newValue, oldValue);
        }
      } else {
        // Nested selector — fire if the top-level slice changed
        const currentTopLevel = this.getSliceValue(topLevel);
        const previousTopLevel = this.previousSnapshot[topLevel];
        if (currentTopLevel !== previousTopLevel) {
          sub.callback(newValue, oldValue);
        }
      }
    }
  }

  private notifyAllStateSubscribers(): void {
    for (const sub of this.stateSubscriptions) {
      const newValue = this.getSliceValue(sub.selector);
      sub.callback(newValue, undefined);
    }
  }

  private notifyEventSubscribers(events: GameEvent[]): void {
    for (const event of events) {
      for (const sub of this.eventSubscriptions) {
        if (sub.eventType === event.type) {
          sub.callback(event);
        }
      }
    }
  }
}
